import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { SectionTitle, Card, Field, Area, SaveButton } from "../ui";

function itemsToText(section) {
  const items = section.items || [];
  if (section.type === "checklist") return items.map((i) => (typeof i === "string" ? i : i.text)).join("\n");
  if (section.type === "cards" || section.type === "steps")
    return items.map((i) => `${i.title} | ${i.text}`).join("\n");
  return "";
}

function textToItems(type, text) {
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  if (type === "checklist") return lines;
  if (type === "cards" || type === "steps")
    return lines.map((l) => {
      const [title, ...rest] = l.split("|");
      return { title: title.trim(), text: rest.join("|").trim() };
    });
  return [];
}

export default function PageEditor() {
  const [pages, setPages] = useState([]);
  const [slug, setSlug] = useState("");
  const [page, setPage] = useState(null);
  const [itemText, setItemText] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/pages").then((res) => {
      setPages(res.data);
      if (res.data.length) selectPage(res.data[0]);
    });
  }, []);

  const selectPage = (p) => {
    setSlug(p.slug);
    setPage(JSON.parse(JSON.stringify(p)));
    const map = {};
    (p.sections || []).forEach((s, i) => { map[i] = itemsToText(s); });
    setItemText(map);
  };

  const save = async () => {
    setSaving(true);
    const updated = { ...page };
    updated.sections = (page.sections || []).map((s, i) => ({
      ...s,
      items: ["checklist", "cards", "steps"].includes(s.type) ? textToItems(s.type, itemText[i] ?? "") : s.items,
    }));
    const { _id, ...payload } = updated;
    await api.put(`/admin/page/${slug}`, payload);
    toast.success("Page content saved");
    setSaving(false);
  };

  return (
    <div data-testid="admin-pages">
      <SectionTitle title="Page content blocks" subtitle="Edit headlines, copy, checklists, and disclaimers for each page." />

      <div className="flex flex-wrap gap-2 mb-6">
        {pages.map((p) => (
          <button
            key={p.slug}
            data-testid={`page-tab-${p.slug}`}
            onClick={() => { const found = pages.find((x) => x.slug === p.slug); selectPage(found); }}
            className={`rounded-full px-4 py-1.5 text-sm font-medium border transition-colors ${
              slug === p.slug ? "bg-sage text-white border-sage" : "bg-white text-charcoal-600 border-sand-200"
            }`}
          >
            {p.nav_label}
          </button>
        ))}
      </div>

      {page && (
        <div className="space-y-6">
          <Card>
            <h3 className="font-medium text-charcoal-800 mb-4">Hero</h3>
            <div className="space-y-4">
              <Field label="Hero title" testid="page-hero-title" value={page.hero_title} onChange={(v) => setPage({ ...page, hero_title: v })} />
              <Field label="Hero title (Español)" testid="page-hero-title-es" value={page.hero_title_es} onChange={(v) => setPage({ ...page, hero_title_es: v })} />
              <Area label="Hero subtitle" testid="page-hero-subtitle" value={page.hero_subtitle} onChange={(v) => setPage({ ...page, hero_subtitle: v })} />
              <Field label="Hero image URL" value={page.hero_image} onChange={(v) => setPage({ ...page, hero_image: v })} />
              <Area label="Intro paragraph" value={page.intro} onChange={(v) => setPage({ ...page, intro: v })} />
              <Area label="Intro paragraph (Español)" value={page.intro_es} onChange={(v) => setPage({ ...page, intro_es: v })} />
              <Area label="Hero subtitle (Español)" testid="page-hero-subtitle-es" value={page.hero_subtitle_es} onChange={(v) => setPage({ ...page, hero_subtitle_es: v })} />
              <Field label="Feature image URL (place photo shown mid-page)" testid="page-feature-image" value={page.feature_image} onChange={(v) => setPage({ ...page, feature_image: v })} />
              <Area label="Feature image caption" testid="page-feature-caption" value={page.feature_caption} onChange={(v) => setPage({ ...page, feature_caption: v })} />
            </div>
          </Card>

          {(page.sections || []).map((s, i) => (
            <Card key={i}>
              <p className="text-xs uppercase tracking-wide text-sage-700 mb-3">Section · {s.type}</p>
              <div className="space-y-3">
                <Field label="Heading" testid={`page-section-heading-${i}`} value={s.heading} onChange={(v) => {
                  const sections = [...page.sections]; sections[i] = { ...sections[i], heading: v }; setPage({ ...page, sections });
                }} />
                {s.type === "text" && (
                  <Area label="Body" rows={4} value={s.body} onChange={(v) => {
                    const sections = [...page.sections]; sections[i] = { ...sections[i], body: v }; setPage({ ...page, sections });
                  }} />
                )}
                {["checklist", "cards", "steps"].includes(s.type) && (
                  <Area
                    label={s.type === "checklist" ? "Items (one per line)" : "Items (one per line as: Title | Text)"}
                    rows={5}
                    testid={`page-section-items-${i}`}
                    value={itemText[i]}
                    onChange={(v) => setItemText((m) => ({ ...m, [i]: v }))}
                  />
                )}
              </div>
            </Card>
          ))}

          <Card>
            <h3 className="font-medium text-charcoal-800 mb-4">Summary & trust details</h3>
            <div className="space-y-4">
              <Area label="Short summary (shown at top of page)" testid="page-summary" value={page.summary} onChange={(v) => setPage({ ...page, summary: v })} />
              <Area label="Short summary (Español)" testid="page-summary-es" value={page.summary_es} onChange={(v) => setPage({ ...page, summary_es: v })} />
              <Area label="Things that can change (verify-before-travel note)" testid="page-what-changes" value={page.what_can_change} onChange={(v) => setPage({ ...page, what_can_change: v })} />
              <Field label="Last updated (e.g. June 2026)" testid="page-last-updated" value={page.last_updated} onChange={(v) => setPage({ ...page, last_updated: v })} />
            </div>
          </Card>

          <Card>
            <Area label="Page disclaimer (optional)" value={page.disclaimer} onChange={(v) => setPage({ ...page, disclaimer: v })} />
          </Card>

          <SaveButton testid="page-save" onClick={save} saving={saving} />
        </div>
      )}
    </div>
  );
}
